"use client";

import { ScreenImage } from "@/components/gallery/screen-image";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";
import { useHoverCycle } from "@/hooks/use-hover-cycle";
import { CYCLE_FADE_MS, canCycle } from "@/lib/firstparty/hover-cycle";
import type { FpAnimation } from "@/lib/firstparty/types";
import { cn } from "@/lib/utils";

const NO_FRAMES: string[] = [];
/** A screen tile in the grids of the app page and Quick look. */
export const TILE_SIZES = "(min-width: 1280px) 170px, (min-width: 640px) 30vw, 45vw";
/** The tile grids: five across on the app page, four in the narrower Quick look sheet. */
export const TILE_GRID = "grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 xl:grid-cols-5";
export const SHEET_TILE_GRID = "grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 xl:grid-cols-4";

/** 59.6 s is "1:00": rounded to whole seconds before minutes and seconds are split. */
export function fmtDur(seconds: number) {
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** "after onboarding · 4 screens": where a flow starts and its size. */
export function flowMeta(flow: { where?: string | null; screens: unknown[] }) {
  const n = flow.screens.length;
  return [flow.where, `${n} ${n === 1 ? "screen" : "screens"}`].filter(Boolean).join(" · ");
}

/** A flow's own motion clips, shown after its screens. */
export const flowClips = (animations: FpAnimation[], flowName: string) => animations.filter((a) => a.flow === flowName);

/** The filled Walkthrough pill with its length. `pressed` makes it a toggle that is quiet while off. */
export function WalkthroughButton({ duration, pressed, onClick, className }: { duration?: number | null; pressed?: boolean; onClick: () => void; className?: string }) {
  const off = pressed === false;
  return (
    <Button type="button" size="pill" variant={off ? "secondary" : "default"} aria-pressed={pressed} onClick={onClick} className={cn("pl-1.5 pr-5", className)}>
      <span className={cn("grid h-7 w-7 place-items-center rounded-full", off ? "bg-foreground text-background" : "bg-background text-foreground")}>
        <Play className="ml-px size-3.5 fill-current" />
      </span>
      Walkthrough
      {duration ? <span className="tabular-nums opacity-60">{fmtDur(duration)}</span> : null}
    </Button>
  );
}

/** One captured iPhone screen with no device bezel: the picture is the frame. */
export function ScreenTile({
  src,
  alt,
  sizes,
  caption,
  meta,
  detail,
  cycle = NO_FRAMES,
  video = false,
  selected = false,
  onOpen,
}: {
  src: string | null | undefined;
  alt: string;
  sizes: string;
  caption?: string | null;
  meta?: string | null;
  /** A second, muted line under the caption, e.g. where a flow starts. */
  detail?: string | null;
  /** Screens to step through while a mouse rests on the card, starting with `src`. */
  cycle?: string[];
  video?: boolean;
  selected?: boolean;
  onOpen?: () => void;
}) {
  const frames = cycle.length > 1 && cycle[0] === src ? cycle : NO_FRAMES;
  const { index, prev, hovering, onPointerEnter, onPointerLeave } = useHoverCycle(frames, sizes);
  const imageClass = "select-none object-cover object-top transition-transform duration-500 ease-out group-hover/tile:scale-[1.015]";
  return (
    <figure className="group/tile min-w-0">
      <button
        type="button"
        onClick={onOpen}
        onPointerEnter={frames.length > 1 ? onPointerEnter : undefined}
        onPointerLeave={frames.length > 1 ? onPointerLeave : undefined}
        aria-label={`${video ? "Play" : "Open"} ${alt}`}
        aria-pressed={selected || undefined}
        className={cn(
          "relative block aspect-[9/19.5] w-full overflow-hidden rounded-[clamp(12px,7%,22px)] bg-muted outline-none",
          "ring-1 ring-black/[0.06] dark:ring-white/[0.08] [.terminal_&]:ring-emerald-500/30",
          "transition-[transform,box-shadow] duration-300 ease-[cubic-bezier(.22,1,.36,1)]",
          "hover:-translate-y-1 hover:shadow-[0_22px_40px_-22px_rgba(0,0,0,0.55)]",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground",
          selected && "outline-2 outline-offset-2 outline-foreground"
        )}
      >
        {frames.length > 1 ? (
          // The screen handing over blurs away under the next one, which comes in sharp; both share the frame.
          [prev, index].map((i, layer) =>
            i === null ? null : (
              <span
                key={i}
                className="absolute inset-0"
                style={prev === null ? undefined : { animation: layer === 0 ? `screen-cycle-out ${CYCLE_FADE_MS}ms ease-out forwards` : `screen-cycle-in ${CYCLE_FADE_MS}ms ease-out both` }}
              >
                <ScreenImage src={frames[i]} sizes={sizes} draggable={false} className={imageClass} />
              </span>
            )
          )
        ) : src ? (
          <ScreenImage src={src} sizes={sizes} draggable={false} className={imageClass} />
        ) : null}
        {frames.length > 1 && (
          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-x-3 bottom-2.5 flex gap-1 opacity-0 transition-opacity duration-300",
              hovering && "opacity-100"
            )}
          >
            {frames.map((frame, i) => (
              <span
                key={frame}
                className={cn(
                  "h-[3px] flex-1 rounded-full shadow-[0_0_0_0.5px_rgba(0,0,0,0.18)] transition-colors duration-300",
                  i <= index ? "bg-white" : "bg-white/45"
                )}
              />
            ))}
          </span>
        )}
        {video && (
          <span className="pointer-events-none absolute inset-0 grid place-items-center bg-black/15">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-white/90 text-black shadow-lg backdrop-blur">
              <Play className="ml-0.5 h-4 w-4 fill-current" />
            </span>
          </span>
        )}
      </button>
      {(caption || meta) && (
        <figcaption className="mt-2.5 px-0.5">
          <span className="flex min-w-0 items-baseline justify-between gap-2">
            {caption ? <span className="truncate text-[13px] text-foreground/80">{caption}</span> : <span />}
            {meta ? <span className="shrink-0 text-[12px] tabular-nums text-muted-foreground">{meta}</span> : null}
          </span>
          {detail && <span className="mt-0.5 block truncate text-[12px] text-muted-foreground">{detail}</span>}
        </figcaption>
      )}
    </figure>
  );
}

type GalleryFilterOption = { value: string; label: string; count?: number };

/**
 * One set of filter choices, shown as a dropdown on phones and as a row of chips from 640 px.
 * `chipsClassName` lays out the chip row; it is hidden below 640 px.
 */
export function GalleryFilter({
  value,
  options,
  onChange,
  label,
  selectClassName,
  chipsClassName,
}: {
  value: string;
  options: GalleryFilterOption[];
  onChange: (value: string) => void;
  label: string;
  selectClassName?: string;
  chipsClassName?: string;
}) {
  return (
    <>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          className={cn("h-10 w-full gap-2 rounded-full border-0 bg-muted/70 px-4 text-[15px] font-medium shadow-none sm:hidden", selectClassName)}
          aria-label={label}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
              {typeof o.count === "number" && <span className="ml-1.5 tabular-nums text-muted-foreground">{o.count}</span>}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className={cn("hidden", chipsClassName)}>
        {options.map((o) => (
          <GalleryChip key={o.value} active={value === o.value} label={o.label} count={o.count} onClick={() => onChange(o.value)} />
        ))}
      </div>
    </>
  );
}

/** Quiet pill filter. Counts sit in a lighter tone so the label reads first. */
function GalleryChip({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full px-4 text-[13px] font-medium outline-none transition-colors",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground",
        active
          ? "bg-foreground text-background"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      {label}
      {typeof count === "number" && (
        <span className={cn("tabular-nums", active ? "text-background/60" : "text-muted-foreground/60")}>{count}</span>
      )}
    </button>
  );
}

export function MotionGrid({ animations, className = TILE_GRID }: { animations: FpAnimation[]; className?: string }) {
  return (
    <div className={className}>
      {animations.map((animation, i) => (
        <AnimationTile key={`${animation.mp4_url}-${i}`} animation={animation} />
      ))}
    </div>
  );
}

/** Plays while a mouse rests on it (not with reduced motion); a tap, click or key press starts or stops it. A page of loops stays still until asked. */
export function AnimationTile({ animation }: { animation: FpAnimation }) {
  const play = (video: HTMLVideoElement | null) => void video?.play().catch(() => undefined);
  const toggle = (video: HTMLVideoElement | null) => (video?.paused ? play(video) : video?.pause());
  return (
    <figure className="min-w-0">
      <button
        type="button"
        aria-label={`Play or pause ${animation.name || "UI animation"}`}
        className="relative block aspect-[9/19.5] w-full overflow-hidden rounded-[clamp(12px,7%,22px)] bg-muted outline-none ring-1 ring-black/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground dark:ring-white/[0.08]"
        onPointerEnter={(event) => event.pointerType === "mouse" && canCycle(window.matchMedia?.bind(window)) && play(event.currentTarget.querySelector("video"))}
        onPointerLeave={(event) => event.pointerType === "mouse" && event.currentTarget.querySelector("video")?.pause()}
        onClick={(event) => toggle(event.currentTarget.querySelector("video"))}
      >
        <video
          src={animation.mp4_url}
          poster={animation.poster_url || undefined}
          loop
          muted
          playsInline
          preload="metadata"
          aria-label={animation.name || "UI animation"}
          className="h-full w-full object-cover object-top"
        />
      </button>
      {(animation.name || animation.kind) && (
        <figcaption className="mt-2.5 truncate px-0.5 text-[13px] text-foreground/80">
          {animation.name || animation.kind?.replace(/_/g, " ")}
        </figcaption>
      )}
    </figure>
  );
}
